/** Re-encode large images at full resolution — smaller files without shrinking pixels. */
export const IMAGE_OPTIMIZE_ABOVE_BYTES = 400 * 1024;
export const IMAGE_JPEG_QUALITY = 0.92;
export const IMAGE_WEBP_QUALITY = 0.9;
/** Visitor check-in stores a data URL when cloud upload is unavailable; keep JSON under Axum's 2MB limit. */
export const INLINE_PHOTO_MAX_EDGE = 960;
export const INLINE_PHOTO_JPEG_QUALITY = 0.84;
export const INLINE_PHOTO_MAX_DATA_URL_CHARS = 900_000;

const IMAGE_EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  heic: 'image/heic',
  heif: 'image/heif',
};

export type PrepareImageOptions = {
  filename?: string;
};

/** Resolve a usable image MIME type from `file.type` or the filename extension. */
export function resolveImageMimeType(file: { type?: string; name?: string }): string {
  const type = (file.type ?? '').trim().toLowerCase();
  if (type.startsWith('image/')) {
    return type === 'image/jpg' ? 'image/jpeg' : type;
  }
  const name = (file.name ?? '').trim().toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  return IMAGE_EXT_MIME[ext] ?? '';
}

export function isHeicImageType(type: string): boolean {
  const normalized = type.trim().toLowerCase();
  return normalized === 'image/heic' || normalized === 'image/heif';
}

export function readImageFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const mime = resolveImageMimeType({
      type: file.type,
      name: file instanceof File ? file.name : '',
    });
    if (!mime) {
      reject(new Error('Please choose an image file.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Could not read the image.'));
    };
    reader.onerror = () => reject(new Error('Could not read the image.'));
    reader.readAsDataURL(file);
  });
}

function swapExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'photo';
  return `${base}.${ext}`;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not process the image.'));
    img.src = dataUrl;
  });
}

function withResolvedImageType(file: File): File {
  const resolved = resolveImageMimeType(file);
  if (!resolved || file.type.toLowerCase() === resolved) return file;
  return new File([file], file.name || 'photo.jpg', { type: resolved });
}

async function sourceToFile(
  source: File | Blob | string,
  options?: PrepareImageOptions
): Promise<File> {
  if (typeof source === 'string') {
    const res = await fetch(source);
    const blob = await res.blob();
    const filename = options?.filename ?? 'photo.jpg';
    return withResolvedImageType(
      new File([blob], filename, {
        type: blob.type || resolveImageMimeType({ name: filename }) || 'image/jpeg',
      })
    );
  }
  if (source instanceof File) {
    return withResolvedImageType(source);
  }
  const filename = options?.filename ?? 'photo.jpg';
  return withResolvedImageType(
    new File([source], filename, {
      type: source.type || resolveImageMimeType({ name: filename }) || 'image/jpeg',
    })
  );
}

function shouldSkipOptimization(file: File): boolean {
  const type = file.type.toLowerCase();
  return type === 'image/gif' || type === 'image/svg+xml';
}

function isAlreadyEfficient(file: File): boolean {
  if (file.size > IMAGE_OPTIMIZE_ABOVE_BYTES) return false;
  const type = file.type.toLowerCase();
  return type === 'image/jpeg' || type === 'image/webp';
}

async function imageHasTransparency(
  img: HTMLImageElement,
  width: number,
  height: number
): Promise<boolean> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(img, 0, 0, width, height);
  const data = ctx.getImageData(0, 0, width, height).data;
  const pixelCount = width * height;
  const step = Math.max(1, Math.floor(pixelCount / 12_000));
  for (let i = 3; i < data.length; i += 4 * step) {
    if (data[i] < 255) return true;
  }
  return false;
}

async function downscaleToJpegDataUrl(
  dataUrl: string,
  maxEdge: number,
  quality: number
): Promise<string> {
  const img = await loadImage(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) {
    throw new Error('Could not process the image.');
  }
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not process the image.');
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

async function reencodeAtFullResolution(file: File): Promise<File | null> {
  const dataUrl = await readImageFileAsDataUrl(file);
  const img = await loadImage(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);

  const originalName = file.name.trim() || 'photo.jpg';
  const isPng = file.type.toLowerCase() === 'image/png';
  const hasAlpha = isPng ? await imageHasTransparency(img, width, height) : false;

  if (hasAlpha) {
    const webp = await canvasToBlob(canvas, 'image/webp', IMAGE_WEBP_QUALITY);
    if (webp && webp.size > 0) {
      return new File([webp], swapExtension(originalName, 'webp'), { type: 'image/webp' });
    }
    const png = await canvasToBlob(canvas, 'image/png');
    if (png) {
      return new File([png], swapExtension(originalName, 'png'), { type: 'image/png' });
    }
    return null;
  }

  const jpeg = await canvasToBlob(canvas, 'image/jpeg', IMAGE_JPEG_QUALITY);
  if (!jpeg) return null;
  return new File([jpeg], swapExtension(originalName, 'jpg'), { type: 'image/jpeg' });
}

/**
 * Prepare an image for upload or inline storage.
 * Keeps original pixel dimensions; re-encodes at high quality when the file is unusually large.
 */
export async function prepareImageFile(
  source: File | Blob | string,
  options?: PrepareImageOptions
): Promise<File> {
  const file = await sourceToFile(source, options);

  if (!resolveImageMimeType(file)) {
    throw new Error('Please choose an image file (JPEG, PNG, or WebP).');
  }
  if (shouldSkipOptimization(file)) {
    return file;
  }
  if (isAlreadyEfficient(file)) {
    return file;
  }

  try {
    const reencoded = await reencodeAtFullResolution(file);
    if (!reencoded) {
      if (isHeicImageType(file.type)) {
        throw new Error(
          'This photo format (HEIC) is not supported here. Choose a JPEG or PNG, or take a photo with the camera.'
        );
      }
      return file;
    }
    if (file.size <= IMAGE_OPTIMIZE_ABOVE_BYTES && reencoded.size >= file.size) {
      return file;
    }
    return reencoded;
  } catch (err) {
    if (isHeicImageType(file.type)) {
      throw new Error(
        'This photo format (HEIC) is not supported here. Choose a JPEG or PNG, or take a photo with the camera.'
      );
    }
    throw err;
  }
}

/** JPEG data URL small enough to send inline (visitor check-in without cloud storage). */
export async function prepareInlineImageDataUrl(
  source: File | Blob | string,
  options?: PrepareImageOptions
): Promise<string> {
  const file = await prepareImageFile(source, options);
  const dataUrl = await readImageFileAsDataUrl(file);
  if (dataUrl.length <= INLINE_PHOTO_MAX_DATA_URL_CHARS) {
    const img = await loadImage(dataUrl);
    const longest = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
    if (longest <= INLINE_PHOTO_MAX_EDGE) {
      return dataUrl;
    }
  }
  return downscaleToJpegDataUrl(dataUrl, INLINE_PHOTO_MAX_EDGE, INLINE_PHOTO_JPEG_QUALITY);
}

/** Data URL preview for the prepared image (same optimization rules as upload). */
export async function prepareImageDataUrl(
  source: File | Blob | string,
  options?: PrepareImageOptions
): Promise<string> {
  const file = await prepareImageFile(source, options);
  return readImageFileAsDataUrl(file);
}

/** @deprecated Use {@link prepareImageDataUrl} */
export async function prepareProfilePhoto(source: File | string): Promise<string> {
  return prepareImageDataUrl(source);
}

/** @deprecated Use {@link prepareImageFile} — no longer downscales pixel dimensions. */
export function compressDataUrl(
  dataUrl: string,
  _maxSize?: number,
  _quality?: number
): Promise<string> {
  return prepareImageDataUrl(dataUrl);
}
